// Receive-only live sync for Lists, Templates, and Checklists (Sharing plan Task 7.3).
// Mutations remain REST requests. A missed frame heals on the page's focus refetch.

import { useEffect, useRef } from "react";
import type { ResourceKind } from "@checklist/shared";
import { getAccessToken, getRefreshToken, isSignedIn, refreshSession } from "../lib/auth.js";
import { buildLiveSyncQuery } from "../lib/liveSyncParams.js";
import { getGuestIdentity, getShareSession } from "../lib/shareSession.js";

const WS_BASE = (import.meta.env.VITE_WS_URL as string | undefined)?.replace(/\/+$/, "");
const MAX_RECONNECT = 5;

/** Per-item check-off frame; checklists only (PLAN.md Task 4.2, §4.3). */
export interface ChecklistLiveItemMessage {
  type: "item";
  checklistId: string;
  sectionIdx: number;
  /** null/omit = ungrouped items in the section. */
  subgroupId?: string | null;
  itemId: string;
  checked: boolean;
  checkedBy?: string;
  screenName: string;
  /** Absent on frames from servers that predate the check-off version bump. */
  version?: number;
}

export interface ResourceLiveMessage {
  type: "resource";
  kind: ResourceKind;
  id: string;
  version: number;
  updatedByName: string;
}

export interface ResourceLiveGate {
  memberCount?: number;
  activeShareCount?: number;
}

export interface ResourceLiveHandlers {
  onItem?: (message: ChecklistLiveItemMessage) => void;
  onResource?: (message: ResourceLiveMessage) => void;
}

/**
 * Subscribe to invalidation and checklist-item frames for one resource.
 * A connection is useful only for a multi-member resource or one with an
 * active share link. A signed-in user sends a fresh Cognito token and, when
 * this tab opened a share link, that share token too. Anonymous visitors
 * connect with the share token and guest identity.
 */
export function useResourceLive(
  kind: ResourceKind,
  id: string | undefined,
  gate: ResourceLiveGate,
  handlers: ResourceLiveHandlers
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const memberCount = gate.memberCount ?? 0;
  const activeShareCount = gate.activeShareCount ?? 0;

  useEffect(() => {
    if (!id || !WS_BASE) return;
    const resourceId = id!;
    if (memberCount < 2 && activeShareCount < 1) return;

    let closed = false;
    let ws: WebSocket | null = null;
    let connecting = false;
    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function clearReconnect() {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    }

    function scheduleReconnect() {
      if (closed || reconnectAttempt >= MAX_RECONNECT) return;
      const delay = Math.min(1000 * 2 ** reconnectAttempt, 16000);
      reconnectAttempt += 1;
      clearReconnect();
      reconnectTimer = setTimeout(() => {
        void connect();
      }, delay);
    }

    async function connect() {
      if (closed || connecting || ws) return;
      connecting = true;
      try {
        let token = isSignedIn() ? getAccessToken() : null;
        if (!token && getRefreshToken()) {
          try {
            await refreshSession();
          } catch {
            // A share token below can still authorize this connection.
          }
          token = isSignedIn() ? getAccessToken() : null;
        }
        if (closed) return;

        const shareToken = getShareSession(kind, resourceId);
        const query = buildLiveSyncQuery({
          kind,
          resourceId,
          accessToken: token,
          shareToken,
          guest: token ? { guestId: "", displayName: null } : getGuestIdentity(),
        });
        if (!query) return;

        try {
          ws = new WebSocket(`${WS_BASE}?${query}`);
        } catch {
          scheduleReconnect();
          return;
        }

        ws.onopen = () => {
          reconnectAttempt = 0;
        };
        ws.onmessage = (event) => {
          try {
            const raw = typeof event.data === "string" ? event.data : String(event.data);
            const message = JSON.parse(raw) as ResourceLiveMessage | ChecklistLiveItemMessage;
            if (message?.type === "resource" && message.kind === kind && message.id === resourceId) {
              handlersRef.current.onResource?.(message);
            } else if (
              message?.type === "item" &&
              kind === "checklist" &&
              message.checklistId === resourceId
            ) {
              handlersRef.current.onItem?.(message);
            }
          } catch {
            // Ignore malformed frames.
          }
        };
        ws.onclose = () => {
          ws = null;
          if (!closed) scheduleReconnect();
        };
        ws.onerror = () => {
          // onclose follows and owns reconnect scheduling.
        };
      } finally {
        connecting = false;
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState !== "visible" || closed || ws !== null) return;
      reconnectAttempt = 0;
      clearReconnect();
      void connect();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    void connect();
    return () => {
      closed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearReconnect();
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      }
    };
  }, [activeShareCount, id, kind, memberCount]);
}
